import {
  type TableColumnFormatNumber,
  getValueFromKeyOfType,
} from '@ipfabric/utils';
import type { RenderedTableCell } from 'components/Table/cells/RenderedTableCell';
import { maybeWrapNonEmptyWithLink } from 'components/Table/cells/helpers/maybeWrapWithLink';
import {
  ARRAY_CELL_MAX_ITEMS,
  maybeWrapWithMore,
} from 'components/Table/cells/helpers/trimLargeArray';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import type { ReactNode } from 'react';
import { unsafeWrapWithReactKeyByIndex } from 'utils/react/unsafeWrapWithReactKeyByIndex';
import type { RenderCellContentArguments } from '../model/TableCellContentRenderFunction';

const makeFormatDecimals =
  (decimals?: number) =>
  (value: number | null): string | null => {
    if (decimals === undefined || value === null) {
      return typeof value === 'number' ? value.toString() : value;
    }

    return value.toFixed(decimals);
  };

export const renderNumberCell = <TRow,>(
  format: TableColumnFormatNumber<TRow>,
  { row, context, severity }: RenderCellContentArguments<TRow>,
): RenderedTableCell => {
  const content = getValueFromKeyOfType(row, format.field);

  const formatDecimals = makeFormatDecimals(format.decimals);

  if (Array.isArray(content)) {
    return {
      content: pipe(
        content,
        (a) => a.slice(0, ARRAY_CELL_MAX_ITEMS),
        array.map((c) =>
          maybeWrapNonEmptyWithLink(
            row,
            severity !== null,
            format.createLinkProps,
            !context.disableLink,
          )(formatDecimals(c), c),
        ),
        (a) => a.map(unsafeWrapWithReactKeyByIndex),
        array.reduce<ReactNode, ReactNode | null>(null, (prev, current) =>
          prev === null ? [current] : [prev, ', ', current],
        ),
        maybeWrapWithMore(content),
      ),
    };
  }

  return {
    alignment: 'right',
    content: pipe(
      content,
      formatDecimals,
      maybeWrapNonEmptyWithLink(
        row,
        severity !== null,
        format.createLinkProps,
        !context.disableLinks,
      ),
    ),
  };
};
